/**
 * @module utils/columnSort/syncFromQB/compareTimeUserFilter
 * @description 场景 3：已持久化 column_sort 时，对照组 / 对比时间与 QB 的联动补丁
 */

import { SortableType } from '@dbl/core/chartEditor';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  resolveCompareTimeFieldMeta,
  resolveCompareTimeFilterDisplay,
  type ICompareTimeInput,
} from '../fromQB/buildDimensionSortItemsFromQB';
import {
  createCompareUserGroupColumn,
  shouldShowCompareUserGroup,
} from '../untils/compareUserGroup';
import {
  getColumnSortKey,
  isColumnSortDimension,
  isColumnSortSanitizeOutputEqual,
  isCompareUserFilterColumn,
  normalizeColumnSortItem,
} from '../untils/keys';

/** 无实质变更时返回原数组引用，供 upload patch / getEffectiveColumnSort 避免无意义拷贝 */
function returnColumnSortIfUnchanged(
  list: IColumnSortItem[],
  next: IColumnSortItem[],
): IColumnSortItem[] {
  return isColumnSortSanitizeOutputEqual(list, next) ? list : next;
}

/**
 * 用户筛选变更时：增删对照组虚拟列，保持其位于列表最前
 */
export function applyUserFilterGroupToColumnSort(
  list: IColumnSortItem[],
  columnUserFilters: unknown[] | undefined,
  hasUserFilters?: boolean,
): IColumnSortItem[] {
  const show = shouldShowCompareUserGroup(columnUserFilters, hasUserFilters);
  const hasGroup = list.some(item => isCompareUserFilterColumn(item.column));

  if (!show) {
    return hasGroup
      ? list.filter(item => !isCompareUserFilterColumn(item.column))
      : list;
  }

  if (hasGroup) {
    return list;
  }

  return [createCompareUserGroupColumn(), ...list];
}

function findCompareFieldInSort(
  list: IColumnSortItem[],
  dateFieldKey?: string,
): number {
  return list.findIndex(item => {
    if (!isColumnSortDimension(item)) {
      return false;
    }
    if (isCompareUserFilterColumn(item.column)) {
      return false;
    }
    if (dateFieldKey && item.column !== dateFieldKey) {
      return false;
    }
    return true;
  });
}

/**
 * 对比时间开关 / 日期字段变更时：更新 is_compare_date，或插入/移除独立对比时间列。
 * 维区已有日期字段时仅打标，不改 column_display_*；关闭对比时从 QB 维区还原 display。
 */
export function applyCompareTimeToColumnSort(
  list: IColumnSortItem[],
  compareTime: ICompareTimeInput | undefined,
  columnDimensions: unknown[],
  columnFilters?: unknown[],
): IColumnSortItem[] {
  if (!compareTime?.enabled) {
    let changed = false;
    const dimMap = new Map<string, Record<string, unknown>>();
    columnDimensions.forEach(raw => {
      const row = raw as Record<string, unknown>;
      const key = getColumnSortKey({
        table_code: String(row.table_code ?? 't0'),
        table_id: String(row.table_id ?? ''),
        column: String(row.column_name ?? row.column ?? ''),
        builder_type: SortableType.Dimension,
      });
      dimMap.set(key, row);
    });

    const next: IColumnSortItem[] = [];
    list.forEach(item => {
      if (!item.is_compare_date) {
        next.push(item);
        return;
      }
      const key = getColumnSortKey(item);
      const dim = dimMap.get(key);
      if (dim && isColumnSortDimension(item)) {
        next.push({
          ...item,
          is_compare_date: false,
          column_display_cn: String(
            dim.column_display ?? dim.column_name ?? item.column,
          ),
          column_display_en: String(
            dim.column_display_en ??
              dim.column_display ??
              dim.column_name ??
              item.column,
          ),
        });
        changed = true;
        return;
      }
      changed = true;
    });
    if (!changed) {
      return list;
    }
    return returnColumnSortIfUnchanged(list, next);
  }

  const dateKey = compareTime.dateFieldKey;
  if (!dateKey) {
    return list;
  }

  const dimIdx = findCompareFieldInSort(list, dateKey);
  if (dimIdx >= 0) {
    const marked = normalizeColumnSortItem({
      ...list[dimIdx],
      is_compare_date: true,
    });
    const markedKey = getColumnSortKey(marked);
    const mapped = list
      .map((item, idx) => {
        if (idx === dimIdx) {
          return marked;
        }
        if (
          item.is_compare_date &&
          isColumnSortDimension(item) &&
          (getColumnSortKey(item) === markedKey || item.column === dateKey)
        ) {
          return null;
        }
        return item;
      })
      .filter((item): item is IColumnSortItem => item != null);
    return returnColumnSortIfUnchanged(list, mapped);
  }

  const existingCompareIdx = list.findIndex(
    item => item.is_compare_date && isColumnSortDimension(item),
  );
  if (existingCompareIdx >= 0) {
    const next = [...list];
    next[existingCompareIdx] = normalizeColumnSortItem({
      ...next[existingCompareIdx],
      is_compare_date: true,
    });
    return returnColumnSortIfUnchanged(list, next);
  }

  const meta = resolveCompareTimeFieldMeta(
    dateKey,
    columnDimensions,
    columnFilters,
  );
  const display = resolveCompareTimeFilterDisplay(dateKey, columnFilters);
  const compareItem = normalizeColumnSortItem({
    table_id: meta.table_id,
    table_code: meta.table_code,
    column: meta.column,
    builder_type: SortableType.Dimension,
    column_display_cn: display.column_display_cn,
    column_display_en: display.column_display_en,
    is_compare_date: true,
  });

  const insertIndex =
    list.length > 0 && isCompareUserFilterColumn(list[0].column) ? 1 : 0;
  const next = [...list];
  next.splice(insertIndex, 0, compareItem);
  return returnColumnSortIfUnchanged(list, next);
}
